import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CaretDown, Check } from "@phosphor-icons/react";
import { useLang } from "@/context/LanguageContext";

export const LanguageSwitcher = ({ variant = "light" }) => {
  const { lang, setLang, languages } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const current = languages.find((l) => l.code === lang) || languages[0];
  const isDark = variant === "dark";

  return (
    <div ref={ref} className="relative" data-testid="language-switcher">
      <button
        type="button"
        data-testid="language-switcher-trigger"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`inline-flex items-center gap-1.5 text-xs tracking-luxe uppercase font-medium transition-colors ${
          isDark
            ? "text-white/70 hover:text-white"
            : "text-[#1A1A1A]/70 hover:text-[#8B7355]"
        }`}
      >
        <span>{current.label}</span>
        <CaretDown
          size={12}
          weight="bold"
          className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.ul
            role="listbox"
            data-testid="language-switcher-menu"
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            className={`absolute right-0 mt-3 w-40 py-2 shadow-2xl backdrop-blur-2xl border ${
              isDark
                ? "bg-[#1A1A1A]/95 border-white/10 text-white"
                : "bg-[#FAFAF7]/95 border-black/5 text-[#1A1A1A]"
            }`}
          >
            {languages.map((l) => {
              const active = l.code === lang;
              return (
                <li key={l.code}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={active}
                    data-testid={`language-option-${l.code}`}
                    onClick={() => {
                      setLang(l.code);
                      setOpen(false);
                    }}
                    className={`w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors ${
                      isDark ? "hover:bg-white/10" : "hover:bg-black/5"
                    } ${active ? (isDark ? "text-white" : "text-[#8B7355]") : ""}`}
                  >
                    <span className="flex items-baseline gap-2">
                      <span className="font-medium tracking-wide">{l.label}</span>
                      <span
                        className={`text-[10px] uppercase tracking-luxe ${
                          isDark ? "text-white/40" : "text-black/40"
                        }`}
                      >
                        {l.name}
                      </span>
                    </span>
                    {active && <Check size={14} weight="bold" />}
                  </button>
                </li>
              );
            })}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
};
